import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IOrcamento } from "app/types/IOrcamento";
import { IItemOrcamento } from "app/types/IItemOrcamento";

export type ItemOrcamentoEmAndamento = Pick<IItemOrcamento,
    "Codigo" |
    "Quantidade" |
    "CodigoTipoEntrega" |
    "CodigoFilialRetirada" |
    "CodigoFilialTransferencia" |
    "CodigoTipoFaturamento" |
    "DataPrevisaoEntrega" |
    "DataMarcadaEntrega" |
    "TurnoEntrega" |
    // 482572 — texto pronto do enum (ver Orcamento.cs/ItemOrcamento.cs::DescricaoTurnoEntrega),
    // usado só para derivar a letra que o PRG espera na restauração (ver
    // normalizarTurnoEntregaParaRestauracao em useRecuperarOrcamentoEmAndamento.ts) sem duplicar
    // aqui o mapeamento código→descrição do enum TurnosEntrega.
    "DescricaoTurnoEntrega" |
    "VendaSobEncomenda" |
    "ObservacaoDoItem" |
    "Index" |
    "PrecoProduto" |
    "Ambiente" |
    "Lote" |
    "Carga" |
    // 482572 — tinta: `Codigo` de um item de tinta é o código do COMPONENTE (base/corante, ver
    // ItemPedidoBase.prg::DefinirTinta), não o do produto tinta que o usuário escolheu — esse fica
    // em CodigoProdutoTinta (coluna CODPROTINTA). Para RECRIAR o item na restauração é o par
    // CodigoProdutoTinta + CodigoCorTinta que o servidor espera (ver converterItemParaProdutoMinimo
    // em useRecuperarOrcamentoEmAndamento.ts); `Codigo` continua sendo o do componente porque é
    // por ele que o PRG casa os itens ao reaplicar os campos pendentes
    // (orcamentobase.prg::_RestaurarDadosPendentes → `loItem.oProduto.ObterIdProduto()`).
    "CodigoProdutoTinta" |
    "CodigoCorTinta"
> & {
    // DadosMadeira completo tem varios campos (Altura/Comprimento/Espessura/Largura/TipoPrecoVenda/
    // PercentualPrecoVenda) — só QuantidadeSolicitada foi pedido para o snapshot; os demais não têm
    // edição pendente relevante para este recurso.
    DadosMadeira?: Pick<NonNullable<IItemOrcamento["DadosMadeira"]>, "QuantidadeSolicitada">
}

export type OrcamentoEmAndamento = Pick<IOrcamento,
    "Numero" |
    "TipoOrcamento" |
    // 482572 — nunca muda durante a edição de um orçamento ainda não gravado; usado junto com
    // TipoOrcamento para separar/expirar o arquivo de recuperação por tipo+filial (ver
    // arquivarOrcamentoEmAndamentoPorTipoEFilial em app/redux/store/store.ts) e para só oferecer a
    // recuperação de um órfão da MESMA filial do orçamento aberto (ver useRecuperarOrcamentoEmAndamento.ts).
    "CodigoFilialOrcamento" |
    "CodigoCliente" |
    "CodigoFormaPagamento" |
    "CodigoTipoDocumento" |
    // Resumo do Orçamento (ResumoOrcamento.tsx)
    "TipoOperacao" |
    "PedidoCompraCliente" |
    "FinalidadeVenda" |
    "DataValidade" |
    "ObservacaoNF" |
    "ObservacaoOrcamento" |
    "UsarObservacaoTextoLivreNF" |
    // Endereços (GridEnderecosOrcamento) — Obra
    "IdObra" |
    // Entrega (aba-entrega) — turno/manter reserva/datas na capa
    "TurnoEntrega" |
    "DescricaoTurnoEntrega" |
    "ManterReserva" |
    "DataPrevisaoEntrega" |
    "DataMarcadaEntrega" |
    // Totalizador.tsx — todos os campos editáveis
    "PercentualDesconto" |
    "ValorDesconto" |
    "PercentualDescontoSobreMercadoriasNacionais" |
    "ValorDescontoSobreMercadoriasNacionais" |
    "PercentualDescontoSobreMercadoriasImportadas" |
    "ValorDescontoSobreMercadoriasImportadas" |
    "ValorDespesasInclusas" |
    "ValorAcrescimoFinanceiro" |
    "PercentualSeguroEntrega" |
    "ValorSeguroEntrega" |
    "PercentualFrete" |
    "ValorFrete" |
    "ValorServicoAdicionalDespesa" |
    "AplicaDescontoCashback" |
    "CodigoMotivoDesconto" |
    "IdCanalDeVenda"
> & {
    Itens: ItemOrcamentoEmAndamento[]

    // Entrega (aba-entrega) — sem representação em IOrcamento hoje; capturados via dispatch direto
    // de TelaEntregaContainer.handleAlterarValorEntrega (ver store.ts/comentário abaixo).
    EntregaExpressa?: boolean
    BloquearDesdobramento?: boolean

    // Transportadora (aba-transportadora) — idem, sem representação em IOrcamento hoje; capturados
    // via TelaEntregaContainer.handleAlterarValorTransportadora.
    CodigoTransportadora?: string
    CodigoTransportadoraRedespacho?: string
    TipoFrete?: string
    CodigoRoteiroEntrega?: string
    CodigoIndicadorDePresenca?: string

    // Endereços de Entrega/Cobrança/Faturamento (GridEnderecosOrcamento) — flags vivem só no
    // estado local do GridEnderecosOrcamentoContainer; capturados via dispatch direto de lá.
    OidEnderecoEntrega?: number
    OidEnderecoCobranca?: number
    OidEnderecoFaturamento?: number

    // Filial de destino da Transferência (ResumoOrcamento.tsx) — sem representação em IOrcamento
    // hoje (só existe em ObterResumoOrcamento, ver Resumo.ts); capturado via dispatch direto de lá.
    FilialDestinoCodigo?: string

    // 482572 — timestamp (Date.now()) da última gravação real do snapshot, gravado por
    // app/redux/store/store.ts — persistido junto (mesma chave por aba, sem chave adicional),
    // usado só para expirar o snapshot órfão de outra aba após QUATRO_DIAS_EM_MS (ver
    // localizarOrcamentoEmAndamentoOrfaoDeOutraAba.ts).
    SalvoEm?: number
}

const initialState: OrcamentoEmAndamento = { Itens: [] }

// 491123 — snapshot do orçamento em andamento (itens + cliente + forma de pagamento + tipo de
// documento + numero, quando já gravado), persistido em localStorage (ver
// orcamentoEmAndamentoPersistedReducer em store.ts) para sobreviver ao fechamento do navegador.
// Mesmo princípio de produtosSelecionadosSlice (#218/#219 do APRENDIZADOS): NÃO escuta `resetAll`
// (disparado em todo login) — o propósito desta slice é justamente ser lida logo APÓS o login
// (ver useRecuperarOrcamentoEmAndamento/verificarOrcamentoEmAndamentoAposLogin em _Menu.cshtml);
// escutar resetAll apagaria o snapshot antes dessa checagem rodar.
//
// NÃO escuta mais `orcamentoSlice.actions.limparInformacoesOrcamento` (removido nesta ficha): essa
// action também é disparada ao ENTRAR numa Venda/Outras Saídas/Transferência nova
// (MenuPrincipalContainer.onRenderizarVenda, para resetar o `orcamento` slice em memória antes de
// criar o novo), e o cascade apagava este snapshot exatamente no momento em que
// useRecuperarOrcamentoEmAndamento.ts precisava dele para oferecer a recuperação. A limpeza
// explícita (`limparOrcamentoEmAndamentoAction`) agora é disparada só nos pontos que representam
// "saiu de verdade": concluído (orcamentoConcluidoContainer.tsx, ao ENTRAR na tela), abandono
// confirmado ao navegar para outro menu estando dentro de Venda/Outras Saídas/Transferência
// (MenuPrincipalContainer.confirmarAbandonoDoOrcamento) ou logout com orçamento em andamento
// (PerfilButtonContainer.fazerLogout).
export const orcamentoEmAndamentoSlice = createSlice({
    name: "orcamentoEmAndamento",
    initialState,
    reducers: {
        salvarOrcamentoEmAndamento: (_state, action: PayloadAction<OrcamentoEmAndamento>) => action.payload,
        limparOrcamentoEmAndamento: () => initialState,
        // Mescla parcial usada por componentes cujo valor NÃO é refletido no slice `orcamento`
        // (ou só é refletido depois de "Aplicar"/salvar) — ResumoOrcamento.tsx, aba-entrega/
        // aba-transportadora (via TelaEntregaContainer) e GridEnderecosOrcamentoContainer. Sem
        // isso, o snapshot só capturaria o último valor CONFIRMADO, perdendo justamente a edição
        // ainda não salva que este recurso existe para recuperar (ver extrairSnapshotOrcamentoEmAndamento
        // em store.ts, que preserva estes campos ao reconstruir o snapshot a partir de `orcamento`).
        atualizarCamposPendentesOrcamentoEmAndamento: (state, action: PayloadAction<Partial<OrcamentoEmAndamento>>) => ({
            ...state,
            ...action.payload,
        }),
    }
})

export default orcamentoEmAndamentoSlice.reducer

export const {
    salvarOrcamentoEmAndamento: salvarOrcamentoEmAndamentoAction,
    limparOrcamentoEmAndamento: limparOrcamentoEmAndamentoAction,
    atualizarCamposPendentesOrcamentoEmAndamento: atualizarCamposPendentesOrcamentoEmAndamentoAction,
} = orcamentoEmAndamentoSlice.actions
